import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Loader2, FileText, RefreshCw, Inbox, Users, Mail, Phone, Shirt, Package, Plus, Pencil, Trash2, Upload, X, Save, ShoppingCart, FileSpreadsheet } from "lucide-react";
import { CONTACT } from "@/data/content";
import { SupplyProductsPanel, SupplyOrdersPanel, SupplyQuotesPanel } from "@/pages/SupplyPanels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "efs_admin_token";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1c4f9c] px-6">
      <div className="absolute inset-0 grain-overlay opacity-[0.08]" />
      <form onSubmit={submit} data-testid="admin-login-form" className="relative bg-white rounded-sm p-8 sm:p-10 w-full max-w-md">
        <div className="flex items-center gap-3 mb-2">
          <img src={CONTACT.logo} alt="Elite Facility Solutions" className="h-11 w-auto" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-[#1c4f9c] flex items-center gap-2"><Lock size={22} /> Admin Login</h1>
        <p className="mt-1 text-sm text-slate-500">Sign in to view leads & applications.</p>
        <div className="mt-7 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#1c4f9c] mb-2">Email</label>
            <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none" placeholder="you@email.com" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#1c4f9c] mb-2">Password</label>
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none" placeholder="••••••••" />
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full inline-flex items-center justify-center gap-2 bg-[#e02329] text-white font-bold py-3.5 rounded-sm hover:bg-[#c81e24] transition-colors disabled:opacity-70">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />} Sign In
          </button>
        </div>
      </form>
    </div>
  );
}

const Bool = ({ v }) => v === true ? <span className="text-green-600 font-semibold">Yes</span> : v === false ? <span className="text-slate-400">No</span> : <span className="text-slate-300">—</span>;

const DEFAULT_SIZES = "XS, S, M, L, XL, 2XL, 3XL";
const emptyProduct = { brand: "", label: "", color: "Vintage White", price: 18, sizes: DEFAULT_SIZES, blurb: "", image: "", active: true };

function ProductsPanel({ onAuthError }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // product object or null
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/uniform/products/all`, authHeader());
      setProducts(data);
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
      else toast.error("Could not load products.");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing("new"); setForm(emptyProduct); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ brand: p.brand || "", label: p.label || "", color: p.color || "", price: p.price ?? 18, sizes: (p.sizes || []).join(", "), blurb: p.blurb || "", image: p.image || "", active: p.active !== false });
  };
  const close = () => { setEditing(null); setForm(emptyProduct); };

  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/uniform/products/image`, fd, { headers: { ...authHeader().headers, "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: `${API}/uniform/products/image/${data.image_path}` }));
      toast.success("Image uploaded.");
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
      else toast.error(err.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.brand.trim()) { toast.error("Brand/name is required."); return; }
    if (!form.image.trim()) { toast.error("Please add a product image."); return; }
    setSaving(true);
    const payload = {
      brand: form.brand.trim(),
      label: form.label.trim(),
      color: form.color.trim() || "Vintage White",
      price: parseFloat(form.price) || 0,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      blurb: form.blurb.trim(),
      image: form.image.trim(),
      active: !!form.active,
    };
    try {
      if (editing === "new") await axios.post(`${API}/uniform/products`, payload, authHeader());
      else await axios.put(`${API}/uniform/products/${editing}`, payload, authHeader());
      toast.success("Product saved.");
      close();
      load();
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
      else toast.error(err.response?.data?.detail || "Could not save product.");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.brand}" from the store?`)) return;
    try {
      await axios.delete(`${API}/uniform/products/${p.id}`, authHeader());
      toast.success("Product removed.");
      load();
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
      else toast.error("Could not remove product.");
    }
  };

  const inp = "w-full border border-slate-300 rounded-sm px-3 py-2.5 text-sm focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none";

  return (
    <div data-testid="products-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Manage the uniforms shown in the Employee Portal.</p>
        <button onClick={openNew} data-testid="add-product-btn" className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#1c4f9c] rounded-sm px-4 py-2 hover:bg-[#163f7d] transition-colors">
          <Plus size={16} /> Add Uniform
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#1c4f9c]" size={28} /></div>
      ) : products.length === 0 ? (
        <p className="p-10 text-center text-slate-400 bg-white border border-slate-200 rounded-sm">No uniforms yet. Click "Add Uniform" to create one.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p) => (
            <div key={p.id} data-testid={`product-card-${p.id}`} className={`bg-white border rounded-sm overflow-hidden ${p.active !== false ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
              <div className="aspect-square bg-slate-100 flex items-center justify-center overflow-hidden">
                <img src={p.image} alt={p.brand} className="w-[80%] h-full object-contain" />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-[#1c4f9c] leading-tight">{p.brand}</h3>
                    <p className="text-xs text-slate-400">{p.label}</p>
                  </div>
                  {p.active === false && <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-500 px-2 py-0.5 rounded-sm">Inactive</span>}
                </div>
                <p className="mt-2 text-sm text-slate-600">${Number(p.price).toFixed(2)} · {p.color}</p>
                <p className="mt-1 text-xs text-slate-400">{(p.sizes || []).join(", ")}</p>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1 text-xs font-bold text-[#1c4f9c] border border-slate-200 rounded-sm py-2 hover:border-[#1c4f9c] transition-colors">
                    <Pencil size={13} /> Edit
                  </button>
                  <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="inline-flex items-center justify-center gap-1 text-xs font-bold text-[#e02329] border border-slate-200 rounded-sm px-3 py-2 hover:border-[#e02329] transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="product-modal">
          <div className="bg-white rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white">
              <h3 className="font-bold text-[#1c4f9c] text-lg">{editing === "new" ? "Add Uniform" : "Edit Uniform"}</h3>
              <button onClick={close} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Name / Brand *</label>
                <input data-testid="product-brand" value={form.brand} onChange={up("brand")} className={inp} placeholder="e.g. CityWide" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Label</label>
                  <input data-testid="product-label" value={form.label} onChange={up("label")} className={inp} placeholder="Subcontractor Apparel" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">Color</label>
                  <input data-testid="product-color" value={form.color} onChange={up("color")} className={inp} placeholder="Vintage White" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Price (USD)</label>
                <input data-testid="product-price" type="number" step="0.01" value={form.price} onChange={up("price")} className={inp} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Sizes (comma-separated)</label>
                <input data-testid="product-sizes" value={form.sizes} onChange={up("sizes")} className={inp} placeholder="XS, S, M, L, XL" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description</label>
                <textarea data-testid="product-blurb" value={form.blurb} onChange={up("blurb")} rows={2} className={inp} placeholder="Short description" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Product Image *</label>
                <div className="flex items-center gap-3">
                  {form.image ? (
                    <img src={form.image} alt="preview" className="w-16 h-16 object-contain bg-slate-100 rounded-sm border border-slate-200" />
                  ) : (
                    <div className="w-16 h-16 bg-slate-100 rounded-sm border border-slate-200 flex items-center justify-center text-slate-300"><Shirt size={22} /></div>
                  )}
                  <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#1c4f9c] border border-slate-300 rounded-sm px-3 py-2 cursor-pointer hover:border-[#1c4f9c] transition-colors">
                    {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload size={15} />} Upload
                    <input data-testid="product-image-upload" type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadImage} className="hidden" />
                  </label>
                </div>
                <input data-testid="product-image-url" value={form.image} onChange={up("image")} className={`${inp} mt-2`} placeholder="…or paste an image URL" />
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                <input data-testid="product-active" type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="w-4 h-4" />
                Show in portal (active)
              </label>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={close} className="text-sm font-bold text-slate-500 px-4 py-2.5 hover:text-slate-700">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="save-product-btn" className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#e02329] rounded-sm px-5 py-2.5 hover:bg-[#c81e24] transition-colors disabled:opacity-70">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);
  const [uniforms, setUniforms] = useState([]);
  const [supplyOrders, setSupplyOrders] = useState([]);
  const [supplyQuotes, setSupplyQuotes] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = () => localStorage.getItem(TOKEN_KEY);
  const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [l, a, u, so, sq] = await Promise.all([
        axios.get(`${API}/leads`, authHeader()),
        axios.get(`${API}/applications`, authHeader()),
        axios.get(`${API}/uniform/orders`, authHeader()),
        axios.get(`${API}/supply/orders`, authHeader()),
        axios.get(`${API}/supply/quotes`, authHeader()),
      ]);
      setLeads(l.data);
      setApps(a.data);
      setUniforms(u.data);
      setSupplyOrders(so.data);
      setSupplyQuotes(sq.data);
    } catch (err) {
      if (err.response?.status === 401) { logout(); toast.error("Session expired, please log in again."); }
      else toast.error("Could not load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "Admin | Elite Facility Solutions";
    if (!token()) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, authHeader())
      .then(() => { setAuthed(true); })
      .catch(() => { logout(); });
  }, []);

  useEffect(() => { if (authed) loadData(); }, [authed, loadData]);

  const openResume = async (path) => {
    try {
      const res = await axios.get(`${API}/applications/resume/${path}`, { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (err) {
      if (err.response?.status === 401) { logout(); toast.error("Session expired, please log in again."); }
      else toast.error("Could not open resume.");
    }
  };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#1c4f9c]" size={32} /></div>;
  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={CONTACT.logo} alt="Elite" className="h-9 w-auto" />
            <span className="font-bold text-[#1c4f9c] hidden sm:inline">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadData} data-testid="admin-refresh" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1c4f9c] border-2 border-slate-200 rounded-sm px-4 py-2 hover:border-[#1c4f9c] transition-colors">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#e02329] rounded-sm px-4 py-2 hover:bg-[#c81e24] transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setTab("leads")} data-testid="tab-leads" className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-sm border-2 transition-colors ${tab === "leads" ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600"}`}>
            <Inbox size={16} /> Leads <span className="ml-1 bg-white/20 px-2 rounded-sm">{leads.length}</span>
          </button>
          <button onClick={() => setTab("apps")} data-testid="tab-apps" className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-sm border-2 transition-colors ${tab === "apps" ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600"}`}>
            <Users size={16} /> Applications <span className="ml-1 bg-white/20 px-2 rounded-sm">{apps.length}</span>
          </button>
          <button onClick={() => setTab("uniforms")} data-testid="tab-uniforms" className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-sm border-2 transition-colors ${tab === "uniforms" ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600"}`}>
            <Shirt size={16} /> Uniform Orders <span className="ml-1 bg-white/20 px-2 rounded-sm">{uniforms.length}</span>
          </button>
          <button onClick={() => setTab("products")} data-testid="tab-products" className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-sm border-2 transition-colors ${tab === "products" ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600"}`}>
            <Package size={16} /> Manage Uniforms
          </button>
          <button onClick={() => setTab("supply-products")} data-testid="tab-supply-products" className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-sm border-2 transition-colors ${tab === "supply-products" ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600"}`}>
            <Package size={16} /> Supply Store
          </button>
          <button onClick={() => setTab("supply-orders")} data-testid="tab-supply-orders" className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-sm border-2 transition-colors ${tab === "supply-orders" ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600"}`}>
            <ShoppingCart size={16} /> Supply Orders <span className="ml-1 bg-white/20 px-2 rounded-sm">{supplyOrders.length}</span>
          </button>
          <button onClick={() => setTab("quotes")} data-testid="tab-quotes" className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-sm border-2 transition-colors ${tab === "quotes" ? "bg-[#1c4f9c] border-[#1c4f9c] text-white" : "border-slate-200 text-slate-600"}`}>
            <FileSpreadsheet size={16} /> Quotes <span className="ml-1 bg-white/20 px-2 rounded-sm">{supplyQuotes.length}</span>
          </button>
        </div>

        {tab === "leads" && (
          <div data-testid="leads-panel" className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            {leads.length === 0 ? (
              <p className="p-10 text-center text-slate-400">No leads yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Service</th>
                    <th className="px-4 py-3">Source</th><th className="px-4 py-3">Estimate</th><th className="px-4 py-3">Message</th><th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.map((l) => (
                    <tr key={l.id} className="align-top hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold text-[#1c4f9c]">{l.name}</td>
                      <td className="px-4 py-3 text-slate-600"><div className="flex items-center gap-1"><Mail size={12} /> {l.email}</div><div className="flex items-center gap-1"><Phone size={12} /> {l.phone}</div></td>
                      <td className="px-4 py-3 text-slate-600">{l.service || "—"}</td>
                      <td className="px-4 py-3"><span className="text-xs font-bold uppercase px-2 py-1 rounded-sm bg-[#1c4f9c]/10 text-[#1c4f9c]">{l.source}</span></td>
                      <td className="px-4 py-3 text-slate-600">{l.estimated_monthly ? `$${Number(l.estimated_monthly).toLocaleString()}/mo` : "—"}{l.frequency ? <div className="text-xs text-slate-400 capitalize">{l.frequency}{l.square_footage ? `, ${Number(l.square_footage).toLocaleString()} sqft` : ""}</div> : null}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs">{l.message || "—"}</td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "apps" && (
          <div data-testid="apps-panel" className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            {apps.length === 0 ? (
              <p className="p-10 text-center text-slate-400">No applications yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Name</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Position</th>
                    <th className="px-4 py-3">Exp</th><th className="px-4 py-3">Avail</th><th className="px-4 py-3">Trans</th>
                    <th className="px-4 py-3">Auth</th><th className="px-4 py-3">No Record</th><th className="px-4 py-3">Resume</th><th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {apps.map((a) => (
                    <tr key={a.id} className="align-top hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold text-[#1c4f9c]">{a.name}{a.bio && <div className="text-xs font-normal text-slate-400 max-w-[12rem]">{a.bio}</div>}</td>
                      <td className="px-4 py-3 text-slate-600"><div className="flex items-center gap-1"><Mail size={12} /> {a.email}</div><div className="flex items-center gap-1"><Phone size={12} /> {a.phone}</div></td>
                      <td className="px-4 py-3 text-slate-600">{a.position}</td>
                      <td className="px-4 py-3 text-slate-600">{a.years_experience || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{a.availability || "—"}</td>
                      <td className="px-4 py-3"><Bool v={a.has_transportation} /></td>
                      <td className="px-4 py-3"><Bool v={a.work_authorized} /></td>
                      <td className="px-4 py-3"><Bool v={a.no_criminal_background} /></td>
                      <td className="px-4 py-3">
                        {a.resume_path ? (
                          <button onClick={() => openResume(a.resume_path)} data-testid="resume-btn" className="inline-flex items-center gap-1 text-xs font-bold text-[#e02329] hover:underline">
                            <FileText size={14} /> View
                          </button>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "uniforms" && (
          <div data-testid="uniforms-panel" className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            {uniforms.length === 0 ? (
              <p className="p-10 text-center text-slate-400">No uniform orders yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Employee</th><th className="px-4 py-3">Items</th><th className="px-4 py-3">Qty</th>
                    <th className="px-4 py-3">Free</th><th className="px-4 py-3">Paid</th><th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Delivery</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {uniforms.map((o) => (
                    <tr key={o.id} className="align-top hover:bg-slate-50">
                      <td className="px-4 py-3"><div className="font-semibold text-[#1c4f9c]">{o.employee_name}</div><div className="text-xs text-slate-400">{o.employee_email}</div></td>
                      <td className="px-4 py-3 text-slate-600">{(o.items || []).map((it, k) => <div key={k}>{it.qty}× {it.brand} <span className="text-slate-400">({it.color}, {it.size})</span></div>)}</td>
                      <td className="px-4 py-3 text-slate-600">{o.total_qty}</td>
                      <td className="px-4 py-3 text-slate-600">{o.free_applied}{o.promo_applied ? <span className="block text-xs text-[#e02329]">promo</span> : null}</td>
                      <td className="px-4 py-3 text-slate-600">{o.payable_qty}</td>
                      <td className="px-4 py-3 text-slate-600">{o.amount ? `$${Number(o.amount).toFixed(2)}` : "Free"}{o.shipping_fee ? <div className="text-xs text-slate-400">incl. ${Number(o.shipping_fee).toFixed(2)} ship</div> : null}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {o.delivery_method === "ship" ? (
                          <div>
                            <span className="text-xs font-bold uppercase px-2 py-1 rounded-sm bg-[#e02329]/10 text-[#e02329]">Ship</span>
                            {o.shipping_address ? (
                              <div className="mt-1 text-xs text-slate-500 max-w-[13rem]">
                                {o.shipping_address.name}<br />
                                {o.shipping_address.line1}{o.shipping_address.line2 ? `, ${o.shipping_address.line2}` : ""}<br />
                                {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}
                                {o.shipping_address.phone ? <><br />{o.shipping_address.phone}</> : null}
                              </div>
                            ) : <div className="mt-1 text-xs text-slate-400">{o.ship_state || ""} · address at payment</div>}
                          </div>
                        ) : (
                          <span className="text-xs font-bold uppercase px-2 py-1 rounded-sm bg-blue-100 text-[#1c4f9c]">Pickup</span>
                        )}
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs font-bold uppercase px-2 py-1 rounded-sm ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : o.payment_status === "not_required" ? "bg-blue-100 text-[#1c4f9c]" : "bg-amber-100 text-amber-700"}`}>{o.payment_status === "not_required" ? "free" : o.status}</span></td>
                      <td className="px-4 py-3 text-slate-400 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {tab === "products" && (
          <ProductsPanel onAuthError={() => { logout(); toast.error("Session expired, please log in again."); }} />
        )}

        {tab === "supply-products" && (
          <SupplyProductsPanel onAuthError={() => { logout(); toast.error("Session expired, please log in again."); }} />
        )}

        {tab === "supply-orders" && <SupplyOrdersPanel orders={supplyOrders} />}

        {tab === "quotes" && <SupplyQuotesPanel quotes={supplyQuotes} />}
      </main>
    </div>
  );
}
